import React, { useState, useEffect } from 'react';

// Need to define more parameters on our cookieconsent_status cookie
const CONSENT_COOKIE_ALLOW = `cookieconsent_status=allow`;
const CONSENT_COOKIE_DENY = `cookieconsent_status=deny`;

export const ConsentContext = React.createContext({
  consentStatus: { showConsentBanner: false, allow: false },
  setConsentCookie: () => {}
});

/**
 * Manages consent status for the site
 * 
 * `consentStatus.showConsentBanner` tracks whether or not the consent banner should be shown
 * `consentStatus.allow` tracks the user's consent selection
 * 
 * On initial render, both values are false.
 * 
 * The useEffect hook determines the initial status of consent
 *   - If CONSENT_COOKIE_ALLOW is set, don't show the consent banner and set allow=true
 *   - If CONSENT_COOKIE_DENY is set, don't show the consent banner and set allow=false
 *   - If neither are set, show the consent banner and set allow=false
 * 
 * `consentStatus.allow` is used by children components to determine whether or not
 * they can run tracking code
 * 
 * `setConsentCookie` is called by the ConsentBanner. It will close the banner, set
 * the `allow` value according, and set the appropriate cookie value
 * 
 * While we're still useing cookieconsent for the consent banner, `showConsentBanner`
 * doesn't have any effect on the display of the cookieconsent banner since it manages
 * that state itself. But it will be useful when we have our own banner component to manage
 * 
 * @param {Object} props.children Children
 */
export const ConsentProvider = ({ children }) => {
  const [consentStatus, setConsentStatus] = useState({
    showConsentBanner: false,
    allow: false
  });

  useEffect(() => {
    if (document.cookie.indexOf(CONSENT_COOKIE_ALLOW) >= 0) {
      setConsentStatus({ showConsentBanner: false, allow: true });
    } else if (document.cookie.indexOf(CONSENT_COOKIE_DENY) >= 0) {
      setConsentStatus({ showConsentBanner: false, allow: false });
    } else {
      setConsentStatus({ showConsentBanner: true, allow: false });
    }
  }, []);
  
  const setConsentCookie = allow => {
    const cookieParams = `;path=/;expires=${new Date(Date.now() + 31536000*1000).toUTCString()};`;
    if (allow) {
      document.cookie = `${CONSENT_COOKIE_ALLOW}${cookieParams}`;
      setConsentStatus({ showConsentBanner: false, allow: true });
    } else {
      document.cookie = `${CONSENT_COOKIE_DENY}${cookieParams}`;
      setConsentStatus({ showConsentBanner: false, allow: false });
    }
  };

  return (
    <ConsentContext.Provider value={{ consentStatus, setConsentCookie }}>
      {children}
    </ConsentContext.Provider>
  )
}